import { ReactNode } from "react";

import { Anchor } from "@mantine/core";
import { notifications } from "@mantine/notifications";

import { SUPPORT_EMAIL } from "@/constants/support";

export type NotificationType = "error" | "success" | "warning" | "info";

const NOTIFICATION_COLORS: Record<NotificationType, string> = {
  error: "red.9",
  success: "green.9",
  warning: "yellow.6",
  info: "blue.9",
};

interface ToastOptions {
  title?: string;
  message: string | ReactNode;
  type?: NotificationType;
  autoClose?: boolean | number;
  icon?: ReactNode;
  /**
   * When set, the toast replaces an on-screen notification with the same id instead of stacking
   * duplicates. Mantine drops a second `show` with an id already on screen, so the old one is hidden first.
   */
  id?: string;
}

export function toast({
  title,
  message,
  type = "error",
  autoClose = false,
  icon = undefined,
  id,
}: ToastOptions) {
  if (id) {
    hideToast(id);
  } else {
    notifications.clean();
  }

  return notifications.show({
    id,
    title,
    message,
    color: NOTIFICATION_COLORS[type],
    autoClose,
    "data-testid": `toast-${type}`,
    ...(icon && { icon }),
  });
}

/**
 * Hides the on-screen notification with the given id, if any.
 *
 * @param id The id the notification was shown with.
 */
export function hideToast(id: string) {
  notifications.hide(id);
}

export function showSystemErrorNotification(title: string = "Something went wrong"): string {
  return toast({
    title,
    message: (
      <>
        Please try again. If the error persists,{" "}
        <Anchor fz="sm" c="burgundy.9" href={`mailto:${SUPPORT_EMAIL}`}>
          contact support
        </Anchor>
        .
      </>
    ),
  });
}
